<script setup>
import { onMounted, ref } from 'vue';
import { Link2, LoaderCircle, ShieldCheck } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import MobileShell from '../../components/MobileShell.vue';
import { api } from '../../services/api';
import { saveDeviceId } from '../../services/device-id';
import { cachePlatformReturnUrl } from '../../services/platform-return';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const loading = ref(true);
const bindingRequired = ref(false);
const error = ref('');
const ticket = typeof route.query.ticket === 'string' ? route.query.ticket : '';

function persist(result) {
  localStorage.setItem('u1card-session-token', result.token);
  localStorage.setItem('u1card-profile', JSON.stringify(result.profile));
  if (result.profile?.deviceId) saveDeviceId(result.profile.deviceId, 'epay-sso');
  cachePlatformReturnUrl(result.returnUrl);
}

onMounted(async () => {
  if (!ticket) {
    error.value = t('platform.invalid');
    loading.value = false;
    return;
  }
  try {
    const result = await api.platformSession(ticket);
    persist(result);
    await router.replace('/home');
  } catch (reason) {
    bindingRequired.value = reason.code === 'U1CARD_ACCOUNT_BINDING_REQUIRED';
    if (bindingRequired.value && reason.details?.deviceId) saveDeviceId(reason.details.deviceId, 'epay-sso');
    if (bindingRequired.value) cachePlatformReturnUrl(reason.details?.returnUrl);
    error.value = bindingRequired.value ? '' : (reason.message || t('platform.failed'));
  } finally {
    loading.value = false;
  }
});

function authPath(path) {
  return { path, query: { platformTicket: ticket, redirect: '/home' } };
}
</script>

<template>
  <MobileShell :nav="false">
    <section class="platform-entry">
      <span class="platform-entry__icon"><LoaderCircle v-if="loading" class="spin" :size="34" /><Link2 v-else-if="bindingRequired" :size="34" /><ShieldCheck v-else :size="34" /></span>
      <h1>{{ loading ? t('platform.connecting') : bindingRequired ? t('platform.bindTitle') : t('platform.errorTitle') }}</h1>
      <p v-if="loading">{{ t('platform.connectingHint') }}</p>
      <template v-else-if="bindingRequired">
        <p>{{ t('platform.bindHint') }}</p>
        <RouterLink class="primary-button" :to="authPath('/login')">{{ t('platform.loginBind') }}</RouterLink>
        <RouterLink class="secondary-button" :to="authPath('/register')">{{ t('platform.registerBind') }}</RouterLink>
      </template>
      <template v-else>
        <p class="error">{{ error }}</p>
        <RouterLink class="secondary-button" to="/login">{{ t('auth.login') }}</RouterLink>
      </template>
    </section>
  </MobileShell>
</template>

<style scoped>
.platform-entry { min-height: 74vh; display: flex; align-items: center; justify-content: center; flex-direction: column; text-align: center; }
.platform-entry__icon { display:grid; width:74px; height:74px; place-items:center; margin-bottom:22px; border:1px solid rgba(130,111,255,.45); border-radius:24px; color:#b4a8ff; background:rgba(90,69,254,.18); box-shadow:0 16px 45px rgba(90,69,254,.22); }
h1 { margin:0 0 10px; font-size:23px; } p { max-width:320px; margin:0 0 28px; color:#93929b; line-height:1.65; font-size:13px; }
.primary-button,.secondary-button { width:100%; max-width:340px; margin-bottom:12px; }
.error { color:#ff8a9c; }.spin { animation:spin 1s linear infinite; } @keyframes spin { to { transform:rotate(360deg); } }
</style>
